import moment from 'moment-timezone';
import { asTimeZone } from '../utils/dateFormatter';
import { create, updateField, updateFields, update, remove, getById, getAll, getBySingleQuery, getByIdQuery, getByDoubleQuery, getByQuery } from '../utils/dbUtils';
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberGreaterThanOrEqualToZero, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import Customer from './Customer';
import StatusHistory from './StatusHistory';
import PrescriptionAddOnDetails from './PrescriptionAddOnDetails';
import { collection, documentId, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { db } from '../firebase';
import { toE164PhoneNumber, toPhoneNumberLocal } from '../utils/stringFormatter';
import { toLower } from 'lodash';

class PrescriptionGroup {
    static collectionName = 'prescriptionGroups';

    eRXId = null; // electronic prescription id from the original written prescription (shared by all child prescriptions)

    userId = null; // userId of the customer
    customer = new Customer(null).toDto(); // customer details snapshot

    practiceId = null; // prescribing practice (coveredEntityId)
    pharmacyId = null; // fulfilling pharmacy

    providerId = null; // userId of the provider who prescribed the medication
    providerFirstName = null;
    providerLastName = null;
    providerNPI = null;

    // medication info (snapshotted from the original written prescription)
    medicationId = null;
    productId = null;
    variantId = null;
    name = null; // medication/product name
    strength = null;
    form = null; // form of the medication (e.g., tablet, liquid)
    route = null; // route of administration (e.g., oral, intravenous)
    instructions = null;
    quantity = 1;
    count = 0;
    countUnits = null;
    daysSupply = 0; // days of medication this fill covers; 0 means unset
    medRefId = null;
    addOns = [];

    pdfRef = null; // storage path for the written prescription pdf
    providerSignatureRef = null; // storage path for the prescriber's signature image

    // fill tracking
    totalFills = 0; // total number of fills (original fill + refills). e.g., refills=2 means totalFills=3
    fillsSubmitted = 0; // number of fills that have been submitted to the pharmacy
    fillsFilled = 0; // number of fills that have been filled by the pharmacy
    fillsShipped = 0; // number of fills that have been shipped
    fillsDelivered = 0; // number of fills that have been delivered to the patient
    fillsCompleted = 0; // number of fills that are delivered and paid for
    fillsVoided = 0; // number of fills that have been voided

    // child references (ordered by fillNumber)
    prescriptionIds = []; // ordered array of child prescription document IDs
    orderIds = []; // corresponding order IDs for each child prescription

    // lifecycle
    status = "ACTIVE"; // enum PrescriptionGroupStatuses: ACTIVE, COMPLETED, EXPIRED, VOIDED
    statusHistory = [];

    writtenAt = null; // when the doctor wrote the prescription
    expirationDate = null; // expiration date for the prescription group
    lastFilledAt = null; // most recent fill date
    lastDeliveredAt = null; // most recent delivery date
    nextFillAt = null; // next scheduled fill date
    completedAt = null; // when all fills are completed
    voidedAt = null;

    // flags
    isControlled = false; // indicates if the prescription is for a controlled substance
    isCompleted = false; // all fills have been completed
    isExpired = false;
    isVoided = false;

    // relations
    subscriptionId = null;
    programId = null;
    encounterId = null;

    testMode = false;

    constructor(prescriptionGroup) {
        if (isObjectNotEmpty(prescriptionGroup)) {
            if (isStringNotEmpty(prescriptionGroup.eRXId)) { this.eRXId = prescriptionGroup.eRXId; }

            if (isStringNotEmpty(prescriptionGroup.userId)) { this.userId = prescriptionGroup.userId; }
            if (isObjectNotEmpty(prescriptionGroup.customer)) { this.customer = new Customer(prescriptionGroup.customer).toDto(); }

            if (isStringNotEmpty(prescriptionGroup.practiceId)) { this.practiceId = prescriptionGroup.practiceId; }
            if (isStringNotEmpty(prescriptionGroup.pharmacyId)) { this.pharmacyId = prescriptionGroup.pharmacyId; }

            if (isStringNotEmpty(prescriptionGroup.providerId)) { this.providerId = prescriptionGroup.providerId; }
            if (isStringNotEmpty(prescriptionGroup.providerFirstName)) { this.providerFirstName = prescriptionGroup.providerFirstName; }
            if (isStringNotEmpty(prescriptionGroup.providerLastName)) { this.providerLastName = prescriptionGroup.providerLastName; }
            if (isStringNotEmpty(prescriptionGroup.providerNPI)) { this.providerNPI = prescriptionGroup.providerNPI; }

            if (isStringNotEmpty(prescriptionGroup.medicationId)) { this.medicationId = prescriptionGroup.medicationId; }
            if (isStringNotEmpty(prescriptionGroup.productId)) { this.productId = prescriptionGroup.productId; }
            if (isStringNotEmpty(prescriptionGroup.variantId)) { this.variantId = prescriptionGroup.variantId; }
            if (isStringNotEmpty(prescriptionGroup.name)) { this.name = prescriptionGroup.name; }
            if (isStringNotEmpty(prescriptionGroup.strength)) { this.strength = prescriptionGroup.strength; }
            if (isStringNotEmpty(prescriptionGroup.form)) { this.form = prescriptionGroup.form; }
            if (isStringNotEmpty(prescriptionGroup.route)) { this.route = prescriptionGroup.route; }
            if (isStringNotEmpty(prescriptionGroup.instructions)) { this.instructions = prescriptionGroup.instructions; }
            if (isNumberGreaterThanOrEqualToZero(prescriptionGroup.quantity)) { this.quantity = prescriptionGroup.quantity; }
            if (isNumberGreaterThanOrEqualToZero(prescriptionGroup.count)) { this.count = prescriptionGroup.count; }
            if (isStringNotEmpty(prescriptionGroup.countUnits)) { this.countUnits = prescriptionGroup.countUnits; }
            if (isNumberGreaterThanOrEqualToZero(prescriptionGroup.daysSupply)) { this.daysSupply = prescriptionGroup.daysSupply; }
            if (isStringNotEmpty(prescriptionGroup.medRefId)) { this.medRefId = prescriptionGroup.medRefId; }
            if (isListNotEmpty(prescriptionGroup.addOns)) { this.addOns = prescriptionGroup.addOns.map((addOn) => new PrescriptionAddOnDetails(addOn).toDto()); }
            if (isStringNotEmpty(prescriptionGroup.pdfRef)) { this.pdfRef = prescriptionGroup.pdfRef; }
            if (isStringNotEmpty(prescriptionGroup.providerSignatureRef)) { this.providerSignatureRef = prescriptionGroup.providerSignatureRef; }

            if (isNumberGreaterThanOrEqualToZero(prescriptionGroup.totalFills)) { this.totalFills = prescriptionGroup.totalFills; }
            if (isNumberGreaterThanOrEqualToZero(prescriptionGroup.fillsSubmitted)) { this.fillsSubmitted = prescriptionGroup.fillsSubmitted; }
            if (isNumberGreaterThanOrEqualToZero(prescriptionGroup.fillsFilled)) { this.fillsFilled = prescriptionGroup.fillsFilled; }
            if (isNumberGreaterThanOrEqualToZero(prescriptionGroup.fillsShipped)) { this.fillsShipped = prescriptionGroup.fillsShipped; }
            if (isNumberGreaterThanOrEqualToZero(prescriptionGroup.fillsDelivered)) { this.fillsDelivered = prescriptionGroup.fillsDelivered; }
            if (isNumberGreaterThanOrEqualToZero(prescriptionGroup.fillsCompleted)) { this.fillsCompleted = prescriptionGroup.fillsCompleted; }
            if (isNumberGreaterThanOrEqualToZero(prescriptionGroup.fillsVoided)) { this.fillsVoided = prescriptionGroup.fillsVoided; }

            if (isListNotEmpty(prescriptionGroup.prescriptionIds)) { this.prescriptionIds = prescriptionGroup.prescriptionIds; }
            if (isListNotEmpty(prescriptionGroup.orderIds)) { this.orderIds = prescriptionGroup.orderIds; }

            if (isStringNotEmpty(prescriptionGroup.status)) { this.status = prescriptionGroup.status; }
            if (isListNotEmpty(prescriptionGroup.statusHistory)) { this.statusHistory = prescriptionGroup.statusHistory; }
            else if (isStringNotEmpty(this.status)) { this.statusHistory = StatusHistory.addNewStatus(this.status); }

            if (isNotNullOrUndefined(prescriptionGroup.writtenAt)) { this.writtenAt = prescriptionGroup.writtenAt; }
            if (isNotNullOrUndefined(prescriptionGroup.expirationDate)) { this.expirationDate = prescriptionGroup.expirationDate; }
            if (isNotNullOrUndefined(prescriptionGroup.lastFilledAt)) { this.lastFilledAt = prescriptionGroup.lastFilledAt; }
            if (isNotNullOrUndefined(prescriptionGroup.lastDeliveredAt)) { this.lastDeliveredAt = prescriptionGroup.lastDeliveredAt; }
            if (isNotNullOrUndefined(prescriptionGroup.nextFillAt)) { this.nextFillAt = prescriptionGroup.nextFillAt; }
            if (isNotNullOrUndefined(prescriptionGroup.completedAt)) { this.completedAt = prescriptionGroup.completedAt; }
            if (isNotNullOrUndefined(prescriptionGroup.voidedAt)) { this.voidedAt = prescriptionGroup.voidedAt; }

            if (isBooleanTrue(prescriptionGroup.isControlled)) { this.isControlled = true; }
            if (isBooleanTrue(prescriptionGroup.isCompleted)) { this.isCompleted = true; }
            if (isBooleanTrue(prescriptionGroup.isExpired)) { this.isExpired = true; }
            if (isBooleanTrue(prescriptionGroup.isVoided)) { this.isVoided = true; }

            if (isStringNotEmpty(prescriptionGroup.subscriptionId)) { this.subscriptionId = prescriptionGroup.subscriptionId; }
            if (isStringNotEmpty(prescriptionGroup.programId)) { this.programId = prescriptionGroup.programId; }
            if (isStringNotEmpty(prescriptionGroup.encounterId)) { this.encounterId = prescriptionGroup.encounterId; }

            if (isBooleanTrue(prescriptionGroup.testMode)) { this.testMode = true; }
        }
    }

    toDto = () => {
        return {
            eRXId: this.eRXId,

            userId: this.userId,
            customer: this.customer,

            practiceId: this.practiceId,
            pharmacyId: this.pharmacyId,

            providerId: this.providerId,
            providerFirstName: this.providerFirstName,
            providerLastName: this.providerLastName,
            providerNPI: this.providerNPI,

            medicationId: this.medicationId,
            productId: this.productId,
            variantId: this.variantId,
            name: this.name,
            strength: this.strength,
            form: this.form,
            route: this.route,
            instructions: this.instructions,
            quantity: this.quantity,
            count: this.count,
            countUnits: this.countUnits,
            daysSupply: this.daysSupply,
            medRefId: this.medRefId,
            addOns: this.addOns,
            pdfRef: this.pdfRef,
            providerSignatureRef: this.providerSignatureRef,

            totalFills: this.totalFills,
            fillsSubmitted: this.fillsSubmitted,
            fillsFilled: this.fillsFilled,
            fillsShipped: this.fillsShipped,
            fillsDelivered: this.fillsDelivered,
            fillsCompleted: this.fillsCompleted,
            fillsVoided: this.fillsVoided,

            prescriptionIds: this.prescriptionIds,
            orderIds: this.orderIds,

            status: this.status,
            statusHistory: this.statusHistory,

            writtenAt: this.writtenAt,
            expirationDate: this.expirationDate,
            lastFilledAt: this.lastFilledAt,
            lastDeliveredAt: this.lastDeliveredAt,
            nextFillAt: this.nextFillAt,
            completedAt: this.completedAt,
            voidedAt: this.voidedAt,

            isControlled: this.isControlled,
            isCompleted: this.isCompleted,
            isExpired: this.isExpired,
            isVoided: this.isVoided,

            subscriptionId: this.subscriptionId,
            programId: this.programId,
            encounterId: this.encounterId,

            testMode: this.testMode
        }
    }

    isValid = () => {
        if (isStringEmpty(this.userId)) {
            throw new Error('userId is required.');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await create(PrescriptionGroup.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(PrescriptionGroup.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(PrescriptionGroup.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedPrescriptionGroup) => {
        const updatedObj = new PrescriptionGroup(updatedPrescriptionGroup);
        if (updatedObj.isValid() && id === updatedPrescriptionGroup.id) {
            return await update(PrescriptionGroup.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated prescription group must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(PrescriptionGroup.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(PrescriptionGroup.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getByERxId = async (eRXId) => {
        if (isStringNotEmpty(eRXId)) {
            let matches = await getBySingleQuery(PrescriptionGroup.collectionName, 'eRXId', eRXId, '==');
            if (isListNotEmpty(matches)) {
                return matches[0];
            }

            return null;
        } else {
            throw new Error("eRXId is required.");
        }
    }

    static getAll = async () => {
        return await getAll(PrescriptionGroup.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(PrescriptionGroup.collectionName, fieldName, fieldValue, comparer);
    }

    static getByUserId = async (userId) => {
        return await getBySingleQuery(PrescriptionGroup.collectionName, 'userId', userId, '==');
    }

    static getByUserIdAndStatus = async (userId, status) => {
        return await getByDoubleQuery(PrescriptionGroup.collectionName, 'userId', userId, '==', 'status', status, '==');
    }

    static getBySubscriptionId = async (subscriptionId) => {
        return await getBySingleQuery(PrescriptionGroup.collectionName, 'subscriptionId', subscriptionId, '==');
    }

    static getByUserIdAndSubscriptionId = async (userId, subscriptionId) => {
        return await getByDoubleQuery(PrescriptionGroup.collectionName, 'userId', userId, '==', 'subscriptionId', subscriptionId, '==');
    }

    static getByEncounterId = async (encounterId) => {
        return await getBySingleQuery(PrescriptionGroup.collectionName, 'encounterId', encounterId, '==');
    }

    static getByPharmacyId = async (pharmacyId) => {
        return await getBySingleQuery(PrescriptionGroup.collectionName, 'pharmacyId', pharmacyId, '==');
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(PrescriptionGroup.collectionName, ids, 'in');
    }

    static getByQueryConstraints = async (queryConstraints = []) => {
        let q = query(collection(db, PrescriptionGroup.collectionName), ...queryConstraints);

        const snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static search = async (searchCriteria) => {
        let { firstName, lastName, email, phone, prescriptionGroupId, prescriptionId, orderId, eRXId, userId, subscriptionId, encounterId, productId, pharmacyId } = searchCriteria;

        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(firstName)) {
            queryConstraints.push(where('customer.firstNameLower', '==', toLower(firstName)));
        }
        if (isStringNotEmptyOrZeroLength(lastName)) {
            queryConstraints.push(where('customer.lastNameLower', '==', toLower(lastName)));
        }
        if (isStringNotEmptyOrZeroLength(email)) {
            queryConstraints.push(where('customer.email', '==', toLower(email)));
        }
        if (isStringNotEmptyOrZeroLength(phone)) {
            const cleanPhone = toPhoneNumberLocal(phone);
            const formattedPhone = toE164PhoneNumber(phone);
            queryConstraints.push(where('customer.phone', 'in', [phone, formattedPhone, cleanPhone]));
        }
        if (isStringNotEmptyOrZeroLength(prescriptionGroupId)) {
            queryConstraints.push(where(documentId(), '==', prescriptionGroupId));
        }
        if (isStringNotEmptyOrZeroLength(prescriptionId)) {
            queryConstraints.push(where('prescriptionIds', 'array-contains', prescriptionId));
        }
        if (isStringNotEmptyOrZeroLength(orderId)) {
            queryConstraints.push(where('orderIds', 'array-contains', orderId));
        }
        if (isStringNotEmptyOrZeroLength(eRXId)) {
            queryConstraints.push(where('eRXId', '==', eRXId));
        }
        if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }
        if (isStringNotEmptyOrZeroLength(subscriptionId)) {
            queryConstraints.push(where('subscriptionId', '==', subscriptionId));
        }
        if (isStringNotEmptyOrZeroLength(encounterId)) {
            queryConstraints.push(where('encounterId', '==', encounterId));
        }
        if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productId', '==', productId));
        }
        if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        queryConstraints.push(orderBy('createdAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getCount = async (queryParameters) => {
        const { productId, status, userId, pharmacyId, subscriptionId, programId, encounterId, testMode, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productId', 'in', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productId', '==', productId));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(subscriptionId)) {
            queryConstraints.push(where('subscriptionId', 'in', subscriptionId));
        } else if (isStringNotEmptyOrZeroLength(subscriptionId)) {
            queryConstraints.push(where('subscriptionId', '==', subscriptionId));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(encounterId)) {
            queryConstraints.push(where('encounterId', 'in', encounterId));
        } else if (isStringNotEmptyOrZeroLength(encounterId)) {
            queryConstraints.push(where('encounterId', '==', encounterId));
        }

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = query(collection(db, PrescriptionGroup.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { productId, status, userId, pharmacyId, subscriptionId, programId, encounterId, testMode, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productId', 'in', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productId', '==', productId));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(subscriptionId)) {
            queryConstraints.push(where('subscriptionId', 'in', subscriptionId));
        } else if (isStringNotEmptyOrZeroLength(subscriptionId)) {
            queryConstraints.push(where('subscriptionId', '==', subscriptionId));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(encounterId)) {
            queryConstraints.push(where('encounterId', 'in', encounterId));
        } else if (isStringNotEmptyOrZeroLength(encounterId)) {
            queryConstraints.push(where('encounterId', '==', encounterId));
        }

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, PrescriptionGroup.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, PrescriptionGroup.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, PrescriptionGroup.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;

            let totalCount = await PrescriptionGroup.getCount(queryParameters);
            let totalPages = Math.ceil(totalCount / 10000.0);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                let list = await PrescriptionGroup.getPaginated(queryParameters, 10000, null, next, 'createdAt');

                allRecords = [...allRecords, ...list];

                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default PrescriptionGroup;
